/**
 * Collapsible Live Tracking section showing raw JSONCargo tracking data.
 * Display-only — does not modify any container status.
 */

import { useState, useMemo } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown, Dataflow03, MarkerPin01, Globe02, Truck01 } from "@untitledui/icons";
import type { JSONCargoTrackingData } from "@/hooks/api/use-container-tracking";

// ============================================================================
// HELPERS
// ============================================================================

const formatTrackingDate = (dateStr: string | null): string => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr.replace(" ", "T"));
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

const formatTimeAgo = (dateStr: string | null): string => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr.replace(" ", "T"));
    if (isNaN(d.getTime())) return "";
    const diffMs = Date.now() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString("en-GB");
  } catch {
    return "";
  }
};

const isLive = (lastUpdated: string | null): boolean => {
  if (!lastUpdated) return false;
  try {
    const d = new Date(lastUpdated.replace(" ", "T"));
    return Date.now() - d.getTime() < 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

// ============================================================================
// FIELD ROW
// ============================================================================

function FieldRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-tertiary">{label}</span>
      <span className="text-sm text-primary font-medium">{value || "—"}</span>
    </div>
  );
}

function DateFieldRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-tertiary">{label}</span>
      <span className="text-sm text-primary font-medium">{formatTrackingDate(value)}</span>
    </div>
  );
}

// ============================================================================
// SECTION GROUP
// ============================================================================

function SectionGroup({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof MarkerPin01;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-tertiary" />
        <span className="text-xs font-semibold text-tertiary uppercase tracking-wider">{title}</span>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">{children}</div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export interface LiveTrackingSectionProps {
  trackingData: JSONCargoTrackingData | undefined;
  isLoading: boolean;
  isError: boolean;
  hasContainerNumber: boolean;
  onRefreshTracking?: () => void;
  className?: string;
}

export function LiveTrackingSection({
  trackingData,
  isLoading,
  isError,
  hasContainerNumber,
  onRefreshTracking,
  className = "",
}: LiveTrackingSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const summary = useMemo(() => {
    if (!trackingData) return null;
    const parts: string[] = [];
    if (trackingData.container_status) parts.push(trackingData.container_status);
    if (trackingData.shipped_from && trackingData.shipped_to) {
      parts.push(`${trackingData.shipped_from} → ${trackingData.shipped_to}`);
    }
    if (trackingData.eta_final_destination) {
      const eta = formatTrackingDate(trackingData.eta_final_destination);
      parts.push(`ETA ${eta}`);
    }
    return parts.join(" — ");
  }, [trackingData]);

  const live = trackingData ? isLive(trackingData.last_updated) : false;

  // Empty states
  if (!hasContainerNumber) {
    return (
      <div className={`rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary ${className}`}>
        <div className="flex items-center gap-3">
          <Dataflow03 className="h-5 w-5 text-quaternary" />
          <span className="text-sm text-tertiary">
            Container number not yet assigned. Tracking will begin once available.
          </span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary animate-pulse ${className}`}>
        <div className="flex items-center gap-3">
          <div className="h-5 w-5 rounded bg-secondary" />
          <div className="h-4 w-48 rounded bg-secondary" />
        </div>
      </div>
    );
  }

  if (isError || !trackingData) {
    return (
      <div className={`rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Dataflow03 className="h-5 w-5 text-quaternary" />
            <span className="text-sm text-tertiary">No tracking data available</span>
          </div>
          {onRefreshTracking && (
            <button
              onClick={onRefreshTracking}
              className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline"
            >
              Refresh Tracking
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl bg-primary shadow-xs ring-1 ring-secondary ${className}`}>
      {/* Header — always visible, clickable */}
      <button
        onClick={() => setIsExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsExpanded((v) => !v);
          }
        }}
        className="w-full flex items-center justify-between p-4 text-left cursor-pointer rounded-xl hover:bg-secondary/50 transition-colors"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Dataflow03 className="h-5 w-5 shrink-0 text-brand-600 dark:text-brand-400" />
          <span className="text-sm font-semibold text-primary shrink-0">Live Tracking</span>

          {/* Live badge */}
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
              live
                ? "bg-success-50 text-success-700 dark:bg-success-950/50 dark:text-success-400"
                : "bg-secondary text-tertiary"
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${live ? "bg-success-500 animate-pulse" : "bg-quaternary"}`}
            />
            {live ? "Live" : "Stale"}
          </span>

          {/* Summary */}
          {summary && !isExpanded && (
            <span className="text-xs text-tertiary truncate ml-2">{summary}</span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-3">
          {trackingData.last_updated && (
            <span className="text-xs text-quaternary">{formatTimeAgo(trackingData.last_updated)}</span>
          )}
          <motion.span
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="h-4 w-4 text-tertiary" />
          </motion.span>
        </div>
      </button>

      {/* Expanded body */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-5 pt-1 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 border-t border-secondary">
              {/* Current Status */}
              <SectionGroup icon={Dataflow03} title="Status">
                <FieldRow label="Container Status" value={trackingData.container_status} />
                <FieldRow label="Shipping Line" value={trackingData.shipping_line_name} />
                <FieldRow label="Container Type" value={trackingData.container_type} />
                <FieldRow label="Tare Weight" value={trackingData.tare} />
              </SectionGroup>

              {/* Position */}
              <SectionGroup icon={MarkerPin01} title="Position">
                <FieldRow
                  label="Last Location"
                  value={
                    trackingData.last_location_terminal
                      ? `${trackingData.last_location} — ${trackingData.last_location_terminal}`
                      : trackingData.last_location
                  }
                />
                <FieldRow
                  label="Next Location"
                  value={
                    trackingData.next_location_terminal
                      ? `${trackingData.next_location} — ${trackingData.next_location_terminal}`
                      : trackingData.next_location
                  }
                />
                <DateFieldRow label="ATD Last Location" value={trackingData.atd_last_location} />
                <DateFieldRow label="ETA Next Port" value={trackingData.eta_next_destination} />
                <DateFieldRow label="Last Movement" value={trackingData.last_movement_timestamp} />
              </SectionGroup>

              {/* Route */}
              <SectionGroup icon={Globe02} title="Route">
                <FieldRow
                  label="Origin"
                  value={
                    trackingData.shipped_from_terminal
                      ? `${trackingData.shipped_from} — ${trackingData.shipped_from_terminal}`
                      : trackingData.shipped_from
                  }
                />
                <FieldRow
                  label="Destination"
                  value={
                    trackingData.shipped_to_terminal
                      ? `${trackingData.shipped_to} — ${trackingData.shipped_to_terminal}`
                      : trackingData.shipped_to
                  }
                />
                <FieldRow label="Loading Port" value={trackingData.loading_port} />
                <FieldRow label="Discharging Port" value={trackingData.discharging_port} />
                <DateFieldRow label="ATD Origin" value={trackingData.atd_origin} />
                <DateFieldRow label="ETA Final" value={trackingData.eta_final_destination} />
              </SectionGroup>

              {/* Vessel */}
              <SectionGroup icon={Truck01} title="Vessel">
                <FieldRow label="Current Vessel" value={trackingData.current_vessel_name} />
                <FieldRow label="Current Voyage" value={trackingData.current_voyage_number} />
                <FieldRow label="Last Vessel" value={trackingData.last_vessel_name} />
                <FieldRow label="Last Voyage" value={trackingData.last_voyage_number} />
              </SectionGroup>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
